import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';

// Calendly Integration Component
const CalendlyButton = ({ 
  text = "Reserva tu sesión", 
  variant = "primary", 
  size = "medium",
  className = "",
  showMicrocopy = true,
  icon = "📅"
}) => {
  const { t } = useTranslations();
  const calendlyUrl = "https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session";
  
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 text-center";
  
  const variants = {
    primary: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg",
    secondary: "border-2 border-green-500 text-green-400 hover:bg-green-500/10 hover:text-green-300",
    outline: "border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40",
    accent: "bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg"
  };
  
  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <div className="text-center">
      <a
        href={calendlyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        aria-label="Reservar sesión gratuita de 30 minutos con Tamara Navarrete"
      >
        <span className="mr-2">{icon}</span>
        {text}
      </a>
      {showMicrocopy && (
        <p className="text-sm text-gray-400 mt-2">
          {t('advisory.freeSession.text')}
        </p>
      )}
    </div>
  );
};

const Navigation = () => {
  const { t } = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          <Link to="/" className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0" onClick={closeMobileMenu}>
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          <div className="hidden md:flex space-x-4 lg:space-x-8 flex-shrink-0">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.home')}</Link>
            <Link to="/sobre-mi" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.aboutMe')}</Link>
            <Link to="/servicios" className="text-purple-400 hover:text-purple-300 transition-colors text-sm lg:text-base font-semibold">{t('navigation.services')}</Link>
            <Link to="/casos-estudio" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.caseStudies')}</Link>
            <Link to="/asesoria" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.consulting')}</Link>
            <Link to="/contacto" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.contact')}</Link>
          </div>

          <div className="md:hidden flex-shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block px-3 py-2 text-gray-300 hover:text-white transition-colors" onClick={closeMobileMenu}>{t('navigation.home')}</Link>
              <Link to="/sobre-mi" className="block px-3 py-2 text-gray-300 hover:text-white transition-colors" onClick={closeMobileMenu}>{t('navigation.aboutMe')}</Link>
              <Link to="/servicios" className="block px-3 py-2 text-purple-400 hover:text-purple-300 transition-colors font-semibold" onClick={closeMobileMenu}>{t('navigation.services')}</Link>
              <Link to="/casos-estudio" className="block px-3 py-2 text-gray-300 hover:text-white transition-colors" onClick={closeMobileMenu}>{t('navigation.caseStudies')}</Link>
              <Link to="/asesoria" className="block px-3 py-2 text-gray-300 hover:text-white transition-colors" onClick={closeMobileMenu}>{t('navigation.consulting')}</Link>
              <Link to="/contacto" className="block px-3 py-2 text-gray-300 hover:text-white transition-colors" onClick={closeMobileMenu}>{t('navigation.contact')}</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const ServiciosPage = () => {
  const { t } = useTranslations();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const servicios = [
    {
      icon: "🎯",
      title: t('services.servicesList.strategy.title'),
      description: t('services.servicesList.strategy.description'),
      features: [
        t('services.servicesList.strategy.feature1'),
        t('services.servicesList.strategy.feature2'),
        t('services.servicesList.strategy.feature3'),
        t('services.servicesList.strategy.feature4')
      ]
    },
    {
      icon: "✨",
      title: t('services.servicesList.branding.title'),
      description: t('services.servicesList.branding.description'),
      features: [
        t('services.servicesList.branding.feature1'),
        t('services.servicesList.branding.feature2'),
        t('services.servicesList.branding.feature3'),
        t('services.servicesList.branding.feature4')
      ]
    },
    {
      icon: "📱",
      title: t('services.servicesList.marketing.title'),
      description: t('services.servicesList.marketing.description'),
      features: [
        t('services.servicesList.marketing.feature1'),
        t('services.servicesList.marketing.feature2'),
        t('services.servicesList.marketing.feature3'),
        t('services.servicesList.marketing.feature4')
      ]
    },
    {
      icon: "💻",
      title: t('services.servicesList.ecommerce.title'),
      description: t('services.servicesList.ecommerce.description'),
      features: [
        t('services.servicesList.ecommerce.feature1'),
        t('services.servicesList.ecommerce.feature2'),
        t('services.servicesList.ecommerce.feature3'),
        t('services.servicesList.ecommerce.feature4')
      ]
    }
  ];

  const casosExito = [
    {
      reto: t('services.miniCases.case1.challenge'),
      solucion: t('services.miniCases.case1.solution'),
      resultado: t('services.miniCases.case1.result'),
      contexto: t('services.miniCases.case1.context'),
      icon: "🚀"
    },
    {
      reto: t('services.miniCases.case2.challenge'),
      solucion: t('services.miniCases.case2.solution'),
      resultado: t('services.miniCases.case2.result'),
      contexto: t('services.miniCases.case2.context'),
      icon: "📈"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Header */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
          >
            <span className="mr-2">←</span>
            {t('home.calculator.backHome')}
          </Link>

          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-white mb-6">
              {t('services.hero.title')} <span className="text-purple-400">{t('services.hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('services.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Áreas de Trabajo */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-6">{t('services.workAreas.title')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {servicios.map((servicio, index) => (
              <div key={index} className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">{servicio.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-3">{servicio.title}</h3>
                  <p className="text-gray-300 mb-6">{servicio.description}</p>
                </div>
                
                <ul className="space-y-3">
                  {servicio.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <span className="text-purple-400 text-lg flex-shrink-0">✓</span>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini Casos de Éxito */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-6">{t('services.miniCases.title')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {casosExito.map((caso, index) => (
              <div key={index} className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl border border-green-500/30 p-8">
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">{caso.icon}</div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-red-400 mb-2">{t('services.miniCases.challenge')}</h4>
                    <p className="text-gray-300 text-sm">{caso.reto}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-blue-400 mb-2">{t('services.miniCases.solution')}</h4>
                    <p className="text-gray-300 text-sm">{caso.solucion}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-green-400 mb-2">{t('services.miniCases.result')}</h4>
                    <p className="text-white font-bold text-lg mb-2">{caso.resultado}</p>
                    <p className="text-gray-400 text-xs italic">{caso.contexto}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('services.cta.title')}</h2>
            <p className="text-xl text-gray-300 mb-8">
              {t('services.cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton 
                text={t('services.cta.bookSession')}
                variant="primary"
                size="large"
                showMicrocopy={false}
              />
              <Link
                to="/asesoria"
                className="border-2 border-purple-500 text-purple-400 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-500 hover:text-white transition-all duration-300"
              >
                {t('services.cta.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiciosPage;